import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, HandHeart, Sparkles } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER = `예) 안녕하세요! 서울에 사는 ○○입니다 😊
취미는 등산과 와인이고, 따뜻한 대화를 나눌 친구를 찾고 있어요.
잘 부탁드립니다!`;

export default function WelcomeIntroPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading, refreshUser } = useAuth();
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    // 이미 가입인사를 작성한 회원은 커뮤니티로
    if (user && user.needs_intro === false) {
      navigate("/community", { replace: true });
    }
  }, [loading, isAuthenticated, user, navigate]);

  const handleSubmit = async () => {
    const text = content.trim();
    if (text.length < 10) {
      toast.error("가입인사를 10자 이상 작성해주세요 🌿");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          category: "general",
          title: text.split("\n")[0].slice(0, 60) || "가입인사",
          content: text,
          images: [],
          videos: [],
          is_anonymous: false,
          topic: "intro",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "등록에 실패했습니다.");
      // needs_intro 플래그가 false로 갱신되도록 사용자 정보 새로고침
      await refreshUser();
      toast.success("환영해요! 가입인사가 등록됐어요 🎉");
      navigate("/community", { replace: true });
    } catch (e) {
      toast.error(e.message || "등록에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5EDE5] via-stone-50 to-stone-100 py-10 px-4 pt-24" data-testid="welcome-intro-page">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-3xl shadow-sm border border-stone-200 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-burgundy-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <HandHeart className="w-8 h-8 text-burgundy-600" />
            </div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-stone-900 mb-2">
              반가워요, {user.username || user.name}님!
            </h1>
            <p className="text-sm text-stone-500 leading-relaxed">
              뉴커플링 커뮤니티의 첫 걸음, <strong className="text-burgundy-700">가입인사</strong>를 남겨주세요.<br />
              따뜻한 첫 인사가 좋은 인연의 시작이 됩니다.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[13px] text-amber-800 leading-snug">
              가입인사를 작성하셔야 커뮤니티를 이용하실 수 있어요. 간단한 자기소개를 적어주세요.
            </p>
          </div>

          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={PLACEHOLDER}
            rows={7}
            className="w-full text-[16px] leading-[1.8] text-stone-800 placeholder-stone-400 bg-stone-50 rounded-2xl p-4 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-100 focus:border-burgundy-300 resize-none"
            data-testid="welcome-intro-textarea"
          />
          <div className="text-right text-xs text-stone-400 mt-1.5 mb-4">{content.trim().length}자</div>

          <Button
            onClick={handleSubmit}
            disabled={busy || content.trim().length < 10}
            className="w-full btn-primary py-3.5 text-base rounded-2xl"
            data-testid="welcome-intro-submit"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "가입인사 등록하고 시작하기"}
          </Button>
        </div>
      </div>
    </div>
  );
}
